/**
 * What the frame loop is actually achieving, on the device it is running on (M166, the user's word: "show a framerate
 * display at the top of the screen like for safari sim"): Sable Reserve's meter (`cquach88/safari-simulator`,
 * `src/ui/frameMeter.ts`), carried over whole, its rules and its reasons as written there.
 *
 * The point of this is comparison across devices, so what it reports has to be
 * the same quantity everywhere. Two numbers, because one of them is a lie on
 * its own: a **rate** over a short window, and the **worst single frame** in
 * that window. A phone that renders thirty frames in half a second and then
 * stalls for 200 ms reads 60 fps on the rate alone and is unplayable; the
 * worst frame is what the player is actually complaining about when they say
 * it stutters.
 *
 * The sampler is deliberately free of the DOM so it can be tested: the tests
 * run in node, and an instrument nobody can check is worth nothing. The one
 * function that touches an element is `mountFrameMeter`, which does no
 * arithmetic.
 */

/**
 * How long a window the reading covers, ms.
 *
 * Half a second: long enough that a single unlucky frame does not swing the
 * rate, short enough that the number responds while the player is doing the
 * thing that costs: turning the camera, opening a habitat, running at 128x.
 */
export const FRAME_WINDOW_MS = 500;

/**
 * Intervals longer than this are not frames and are thrown away.
 *
 * A backgrounded tab stops receiving `requestAnimationFrame` entirely, so the
 * first frame after the player comes back is one interval of however long they
 * were away. Counted, it would sit in the worst-frame reading for the whole
 * window and report a stall that never happened on screen. A second is far
 * past any real frame (thirty times a bad one), so nothing genuine is lost.
 */
export const FRAME_DISCARD_MS = 1000;

/** Frames per second at or above which motion reads as smooth. */
const FRAME_RATE_GOOD = 50;
/** Below this it reads as a slideshow rather than as motion. */
const FRAME_RATE_POOR = 25;

export interface FrameReading {
  /** Frames per second across the window: intervals over the time they took. */
  fps: number;
  /** The longest single interval in the window, ms. */
  worstMs: number;
  /** How many intervals the reading is built on. */
  samples: number;
}

/**
 * A rolling window of frame intervals.
 *
 * Given `performance.now()` once per frame it holds the last
 * `FRAME_WINDOW_MS` of them and answers with the rate and the worst.
 * `read()` returns null until it has two frames, because one timestamp is no
 * interval at all and a rate from it would be invented.
 */
export class FrameTimes {
  private readonly intervals: number[] = [];
  private last: number | null = null;
  /**
   * The intervals past `FRAME_DISCARD_MS` in a row, and the last of them (review M181: a device slower than a frame a
   * second threw every interval away and read "… fps" forever). A hidden tab's return is one such interval; two in a
   * row is the device itself, and is read.
   */
  private slow = 0;
  private slowMs = 0;

  /** Records a frame drawn at `now`, in the units `performance.now()` gives. */
  record(now: number): void {
    const previous = this.last;
    this.last = now;
    if (previous === null) return;
    const interval = now - previous;
    // Non-monotonic or absurd clocks are the caller's problem, not a reading.
    if (!(interval > 0)) return;
    if (interval > FRAME_DISCARD_MS) {
      [this.slow, this.slowMs] = [this.slow + 1, interval];
      return;
    }
    this.slow = 0;
    this.intervals.push(interval);
    // Drop from the front until the window is what it claims to be. Held as
    // elapsed time rather than a fixed count, so the window means the same
    // half-second at 30 fps and at 144.
    let total = 0;
    for (const value of this.intervals) total += value;
    while (this.intervals.length > 1 && total - this.intervals[0]! >= FRAME_WINDOW_MS) {
      total -= this.intervals.shift()!;
    }
  }

  read(): FrameReading | null {
    if (this.slow >= 2) return { fps: 1000 / this.slowMs, worstMs: this.slowMs, samples: this.slow };
    if (this.intervals.length === 0) return null;
    let total = 0;
    let worst = 0;
    for (const value of this.intervals) {
      total += value;
      if (value > worst) worst = value;
    }
    if (total <= 0) return null;
    return {
      fps: (this.intervals.length * 1000) / total,
      worstMs: worst,
      samples: this.intervals.length,
    };
  }

  /** Forgets everything, so a reading never spans a gap the player caused. */
  reset(): void {
    this.intervals.length = 0;
    this.last = null;
    this.slow = 0;
  }
}

/** How the reading reads: good, watch, or bad, on the same words the HUD uses. */
export function frameTone(reading: FrameReading): 'good' | 'warn' | 'bad' {
  if (reading.fps >= FRAME_RATE_GOOD) return 'good';
  if (reading.fps >= FRAME_RATE_POOR) return 'warn';
  return 'bad';
}

/**
 * The line the meter shows.
 *
 * Rounded to whole frames, because a tenth of a frame is noise at this window
 * length and a number that jitters in its last digit is harder to read at a
 * glance than one that does not.
 */
export function frameLabel(reading: FrameReading | null): string {
  if (reading === null) return '… fps';
  if (reading.fps < 1) return `under 1 fps · ${Math.round(reading.worstMs)} ms worst`;
  return `${Math.round(reading.fps)} fps · ${Math.round(reading.worstMs)} ms worst`;
}

export interface FrameMeter {
  /** Call once per frame with `performance.now()`. */
  record(now: number): void;
  /** Shows or hides the meter; hidden, it stops sampling as well as drawing. */
  setVisible(on: boolean): void;
  readonly visible: boolean;
}

/**
 * How often the element is written, ms.
 *
 * The reading is recomputed every frame and *written* four times a second.
 * Writing it every frame is a layout and a paint of the one thing on screen
 * whose job is to not cost anything, and a number changing 60 times a second
 * cannot be read anyway.
 */
const METER_REDRAW_MS = 250;

/**
 * Puts a meter on the page and returns the handle the frame loop drives.
 *
 * The element is its own absolutely positioned node under the top bar and the
 * aquarium's bar rather than a cell in either (Sable Reserve's top bar, a flex
 * row, had twice been pushed off the side of a phone by one more thing added
 * to it): being absolute it cannot move anything, and sitting below the bars
 * it covers none of their buttons.
 */
export function mountFrameMeter(parent: HTMLElement, visible: boolean): FrameMeter {
  const node = document.createElement('div');
  node.className = 'frame-meter';
  // Hidden from assistive technology on purpose. It was `role="status"`, which
  // is a live region: a screen reader would have announced a new frame rate
  // four times a second, for ever, over whatever the player was actually
  // trying to hear. It is a diagnostic about the machine rather than anything
  // about the sanctuary, and nothing else on screen depends on it.
  node.setAttribute('aria-hidden', 'true');
  node.hidden = !visible;
  parent.appendChild(node);

  const times = new FrameTimes();
  let shown = visible;
  let lastWrite = 0;
  let painted = '';

  return {
    record(now: number): void {
      if (!shown) return;
      times.record(now);
      if (now - lastWrite < METER_REDRAW_MS) return;
      lastWrite = now;
      const reading = times.read();
      const label = frameLabel(reading);
      if (label === painted) return;
      painted = label;
      node.textContent = label;
      node.dataset.tone = reading ? frameTone(reading) : '';
    },
    setVisible(on: boolean): void {
      shown = on;
      node.hidden = !on;
      // A window that spans the time the meter was switched off would report
      // a stall the player never saw.
      if (on) times.reset();
    },
    get visible(): boolean {
      return shown;
    },
  };
}

/** Where the player's choice is kept between visits (M166; the saves' own prefix, `platform/saves.ts`). */
export const FRAME_METER_KEY = 'seven-seas.frameMeter';

/** Whether the meter is shown, from what was stored: on unless turned off, as Sable Reserve's (a diagnostic nobody can find is one nobody uses). */
export function frameMeterShown(stored: string | null): boolean {
  return stored !== 'off';
}

/** The game menu's item for it, saying which way it is now. */
export function frameMeterItem(on: boolean): string {
  return `Frame rate meter: ${on ? 'on' : 'off'}`;
}
